import {isNullOrEmpty} from "@2d-front-monorepo/core";
import {JSX, memo, useMemo, useRef, useState} from "react";

interface IconProps {
	className?: string;
	icon: string;
	title?: string;
}

// Ligature-based Material Symbols icon. When the `icon` prop changes, the old and new glyphs
// cross-fade (opacity/scale/blur) instead of hard-swapping. First render never animates.
function IconControl({icon, title, className}: IconProps): JSX.Element {
	const cn = useMemo(() => `material-symbols-rounded decoration-icon${className ? ` ${className}` : ""}`, [className]);
	const [outgoing, setOutgoing] = useState<{ glyph: string; id: number } | null>(null);
	const last = useRef(icon);

	// Render-phase adjustment so the very first paint after a swap already shows both glyphs.
	if (icon !== last.current) {
		if (!isNullOrEmpty(icon) && !isNullOrEmpty(last.current)) {
			setOutgoing(prev => ({glyph: last.current, id: (prev?.id ?? 0) + 1}));
		}
		last.current = icon;
	}

	if (isNullOrEmpty(icon)) {
		return <></>;
	}

	const content = outgoing
		? <>
			<span key={`out${outgoing.id}`} className="material-icon__glyph--out" aria-hidden={true}
			      onAnimationEnd={() => setOutgoing(null)}>{outgoing.glyph}</span>
			<span key={`in${outgoing.id}`} className="material-icon__glyph--in">{icon}</span>
		</>
		: icon;
	const swapCn = outgoing ? `${cn} material-icon--swap` : cn;

	if (isNullOrEmpty(title)) {
		return <span className={swapCn} aria-hidden={true}>{content}</span>;
	}

	return <span className={swapCn} title={title} aria-label={title}>{content}</span>;
}

const MaterialIcon: React.FC<IconProps> = memo(IconControl);
export default MaterialIcon;
